import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { csvDownloadDataAtom } from '@/atoms/exporting';
import { fetchClientAtom } from '@/atoms/queries/client';
import { toastDataAtom } from '@/atoms/toast';
import { useCsvUrl } from '@/hooks/useCsvUrl';
import { FetchError } from '@/query/client';
import { MenuItemWithIcon } from '../menu/MenuItemWithIcon';
import { createLinkAndDownload } from './Export.helper';

/**
 * This BOM needs to be prepended to the CSV file to get Excel to render non-ascii characters in a utf-8 encoded file
 * correctly
 */
const UTF_8_BYTE_ORDER_MARKER = new Uint8Array([0xef, 0xbb, 0xbf]);

/**
 * The API refuses exports over its row limit with this status, deliberately without saying what the limit is.
 * Only physical-metric exports (Player Scout, player Match Stats) return it, so the toast copy assumes competition and
 * season filters - revisit the wording if the API applies the cap elsewhere.
 */
const EXPORT_TOO_LARGE_STATUS = 422;

export const ExportCsvMenuItem = () => {
  const { url, fileName } = useAtomValue(csvDownloadDataAtom);
  const { t } = useTranslation('csv');
  const { fetch } = useAtomValue(fetchClientAtom);
  const setToastData = useSetAtom(toastDataAtom);
  const csvUrl = useCsvUrl();

  const handleCsvExport = async () => {
    setToastData({ toastName: 'exportCsvStarted', type: 'success' });

    try {
      const response = await fetch(csvUrl, {
        headers: { accept: 'text/csv' },
      });

      // The response from the server already includes the BOM but sadly this isn't correctly added to the Blob, so we
      // need to strip it off and add again using the following code, otherwise you end up with weird (visible)
      // characters in your CSV.
      const csvString = (await response.text()).replace(UTF_8_BYTE_ORDER_MARKER, '');
      const csvStringBlob = new Blob([UTF_8_BYTE_ORDER_MARKER, csvString], {
        type: response.headers.get('content-type'),
      });
      const dataUrl = URL.createObjectURL(csvStringBlob);
      createLinkAndDownload(dataUrl, fileName);
      setToastData({ toastName: 'exportCsvCompleted', type: 'success' });
    } catch (e) {
      const isTooLarge = e instanceof FetchError && e.status === EXPORT_TOO_LARGE_STATUS;
      setToastData({ toastName: isTooLarge ? 'exportCsvTooLarge' : 'exportCsvCompleted', type: 'error' });
    }
  };

  return <MenuItemWithIcon onClick={handleCsvExport} icon="Download" text={t('export')} disabled={!fileName || !url} />;
};
